import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import { ConsultationHistoryItem, SortConfig, ColumnConfig } from '../types';

interface ConsultationTableProps {
  consultations: ConsultationHistoryItem[];
  selectedIds: string[];
  onSelectionChange: (ids: string[]) => void;
  sortConfig: SortConfig;
  onSortChange: (config: SortConfig) => void;
  columnConfig: ColumnConfig[];
  onColumnConfigChange: (config: ColumnConfig[]) => void;
}

const ConsultationTable = ({
  consultations,
  selectedIds,
  onSelectionChange,
  sortConfig,
  onSortChange,
  columnConfig,
  onColumnConfigChange
}: ConsultationTableProps) => {
  const navigate = useNavigate();
  const [showColumnConfig, setShowColumnConfig] = useState(false);

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      onSelectionChange(consultations.map(c => c.id));
    } else {
      onSelectionChange([]);
    }
  };

  const handleSelectOne = (id: string, checked: boolean) => {
    if (checked) {
      onSelectionChange([...selectedIds, id]);
    } else {
      onSelectionChange(selectedIds.filter(selectedId => selectedId !== id));
    }
  };

  const handleSort = (field: keyof ConsultationHistoryItem) => {
    if (sortConfig.field === field) {
      onSortChange({
        field,
        direction: sortConfig.direction === 'asc' ? 'desc' : 'asc'
      });
    } else {
      onSortChange({ field, direction: 'asc' });
    }
  };

  const getStatusBadge = (status: ConsultationHistoryItem['status']) => {
    const statusConfig = {
      success: { label: 'Sucesso', color: 'bg-success/10 text-success', icon: 'CheckCircle2' },
      api_error: { label: 'Erro de API', color: 'bg-destructive/10 text-destructive', icon: 'XCircle' },
      validation_error: { label: 'Erro de Validação', color: 'bg-warning/10 text-warning', icon: 'AlertCircle' },
      pending: { label: 'Pendente', color: 'bg-muted text-muted-foreground', icon: 'Clock' },
      processing: { label: 'Processando', color: 'bg-accent/10 text-accent', icon: 'Loader2' }
    };

    const config = statusConfig[status];
    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${config.color}`}>
        <Icon name={config.icon} size={12} color="currentColor" />
        {config.label}
      </span>
    );
  };

  const getFreshnessBadge = (freshness: ConsultationHistoryItem['apiDataFreshness']) => {
    const freshnessConfig = {
      current: { label: 'Atualizado', color: 'bg-success/10 text-success', icon: 'CheckCircle2' },
      outdated: { label: 'Desatualizado', color: 'bg-warning/10 text-warning', icon: 'AlertTriangle' },
      requires_update: { label: 'Requer Atualização', color: 'bg-destructive/10 text-destructive', icon: 'RefreshCw' }
    };

    const config = freshnessConfig[freshness];
    return (
      <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs ${config.color}`}>
        <Icon name={config.icon} size={10} color="currentColor" />
        {config.label}
      </span>
    );
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const formatCNPJ = (cnpj: string) => {
    return cnpj.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
  };

  const visibleColumns = columnConfig.filter(col => col.visible);

  return (
    <div className="flex-1 flex flex-col bg-card">
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center gap-3">
          <h3 className="text-sm font-medium text-foreground">
            {consultations.length} consulta{consultations.length !== 1 ? 's' : ''} encontrada{consultations.length !== 1 ? 's' : ''}
          </h3>
          {selectedIds.length > 0 && (
            <span className="text-sm text-muted-foreground">
              ({selectedIds.length} selecionada{selectedIds.length !== 1 ? 's' : ''})
            </span>
          )}
        </div>
        <div className="relative">
          <Button
            variant="outline"
            size="sm"
            iconName="Settings2"
            iconPosition="left"
            onClick={() => setShowColumnConfig(!showColumnConfig)}
          >
            Colunas
          </Button>
          {showColumnConfig && (
            <div className="absolute right-0 top-full mt-2 w-64 bg-card border border-border rounded-md shadow-lg z-10">
              <div className="p-3 border-b border-border">
                <h4 className="text-sm font-medium text-foreground">Configurar Colunas</h4>
              </div>
              <div className="p-3 space-y-2 max-h-80 overflow-y-auto">
                {columnConfig.map((col) => (
                  <Checkbox
                    key={col.id}
                    label={col.label}
                    checked={col.visible}
                    onChange={(e) => {
                      const newConfig = columnConfig.map(c =>
                        c.id === col.id ? { ...c, visible: e.target.checked } : c
                      );
                      onColumnConfigChange(newConfig);
                    }}
                  />
                ))}
              </div>
              <div className="p-3 border-t border-border">
                <Button
                  variant="outline"
                  size="sm"
                  fullWidth
                  onClick={() => setShowColumnConfig(false)}
                >
                  Fechar
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-auto">
        <table className="w-full">
          <thead className="bg-muted sticky top-0 z-10">
            <tr>
              <th className="w-12 px-4 py-3 text-left">
                <Checkbox
                  checked={selectedIds.length === consultations.length && consultations.length > 0}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                  aria-label="Selecionar todas"
                />
              </th>
              {visibleColumns.map((col) => (
                <th
                  key={col.id}
                  className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:bg-muted/80"
                  onClick={() => handleSort(col.id as keyof ConsultationHistoryItem)}
                >
                  <div className="flex items-center gap-2">
                    {col.label}
                    {sortConfig.field === col.id && (
                      <Icon
                        name={sortConfig.direction === 'asc' ? 'ChevronUp' : 'ChevronDown'}
                        size={14}
                        color="var(--color-muted-foreground)"
                      />
                    )}
                  </div>
                </th>
              ))}
              <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Ações
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {consultations.map((consultation) => (
              <tr
                key={consultation.id}
                className="hover:bg-muted/50 transition-colors"
              >
                <td className="px-4 py-3">
                  <Checkbox
                    checked={selectedIds.includes(consultation.id)}
                    onChange={(e) => handleSelectOne(consultation.id, e.target.checked)}
                    aria-label={`Selecionar consulta ${consultation.consultationId}`}
                  />
                </td>
                {visibleColumns.map((col) => {
                  switch (col.id) {
                    case 'consultationId':
                      return (
                        <td key={col.id} className="px-4 py-3">
                          <span className="font-mono text-sm text-foreground">
                            {consultation.consultationId}
                          </span>
                        </td>
                      );
                    case 'companyName':
                      return (
                        <td key={col.id} className="px-4 py-3">
                          <div>
                            <p className="text-sm font-medium text-foreground">
                              {consultation.companyName}
                            </p>
                            <p className="text-xs text-muted-foreground font-mono">
                              {formatCNPJ(consultation.cnpj)}
                            </p>
                          </div>
                        </td>
                      );
                    case 'createdDate':
                      return (
                        <td key={col.id} className="px-4 py-3">
                          <span className="text-sm text-foreground">
                            {formatDate(consultation.createdDate)}
                          </span>
                        </td>
                      );
                    case 'status':
                      return (
                        <td key={col.id} className="px-4 py-3">
                          {getStatusBadge(consultation.status)}
                        </td>
                      );
                    case 'productCount':
                      return (
                        <td key={col.id} className="px-4 py-3">
                          <span className="text-sm text-foreground">
                            {consultation.productCount}
                          </span>
                        </td>
                      );
                    case 'totalTaxAmount':
                      return (
                        <td key={col.id} className="px-4 py-3">
                          <span className="text-sm font-medium text-foreground font-mono">
                            {formatCurrency(consultation.totalTaxAmount)}
                          </span>
                        </td>
                      );
                    case 'ufOrigin':
                      return (
                        <td key={col.id} className="px-4 py-3">
                          <span className="text-sm text-foreground">
                            {consultation.ufOrigin}
                          </span>
                        </td>
                      );
                    case 'ufDestination':
                      return (
                        <td key={col.id} className="px-4 py-3">
                          <span className="text-sm text-foreground">
                            {consultation.ufDestination}
                          </span>
                        </td>
                      );
                    case 'apiDataFreshness':
                      return (
                        <td key={col.id} className="px-4 py-3">
                          {getFreshnessBadge(consultation.apiDataFreshness)}
                        </td>
                      );
                    default:
                      return null;
                  }
                })}
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="Eye"
                      onClick={() => navigate(`/historical-consultation-detail?id=${consultation.id}`)}
                      aria-label="Ver detalhes"
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="RefreshCw"
                      aria-label="Reprocessar"
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="Download"
                      aria-label="Exportar"
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ConsultationTable;